import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import CustomDatePicker from './CustomDatePicker';
import './DateRangeFilter.css';
import {
  setDateFilter,
  removeDateFilter,
  setPendingDateFilter,
  selectDateFilters,
  selectPendingDateFilters
} from '../store/filterSlice';

function DateRangeFilter({ filter, isOnResultPage = false }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const dispatch = useDispatch();
  const dateFilters = useSelector(selectDateFilters);
  const pendingDateFilters = useSelector(selectPendingDateFilters);

  const fieldName = filter.value;
  const appliedFilter = dateFilters[fieldName];
  const currentFilter = isOnResultPage ? (pendingDateFilters[fieldName] || appliedFilter) : appliedFilter;

  const [startDate, setStartDate] = useState(
    currentFilter?.startDate || ''
  );
  const [endDate, setEndDate] = useState(
    currentFilter?.endDate || ''
  );

  // Get today's date for max date restriction
  const maxDate = new Date();

  const handleStartDateChange = (newDate) => {
    setStartDate(newDate);
    let updatedEndDate = endDate;

    // If new start date is after current end date, clear the end date
    if (newDate && endDate) {
      const startDateObj = new Date(newDate);
      const endDateObj = new Date(endDate);
      if (startDateObj > endDateObj) {
        updatedEndDate = '';
        setEndDate('');
      }
    }

    const startDateStr = newDate || '';
    const endDateStr = updatedEndDate || '';

    if (isOnResultPage && (startDateStr || endDateStr)) {
      dispatch(setPendingDateFilter({ fieldName, startDate: startDateStr, endDate: endDateStr }));
    } else if (!isOnResultPage) {
      dispatch(setDateFilter({ fieldName, startDate: startDateStr, endDate: endDateStr }));
    }
  };

  const handleEndDateChange = (newDate) => {
    setEndDate(newDate);
    const startDateStr = startDate || '';
    const endDateStr = newDate || '';

    if (isOnResultPage && (startDateStr || endDateStr)) {
      dispatch(setPendingDateFilter({ fieldName, startDate: startDateStr, endDate: endDateStr }));
    } else if (!isOnResultPage) {
      dispatch(setDateFilter({ fieldName, startDate: startDateStr, endDate: endDateStr }));
    }
  };

  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    dispatch(removeDateFilter(fieldName));
  };

  const hasFilter = currentFilter && (currentFilter.startDate || currentFilter.endDate);
  const hasAppliedFilter = appliedFilter && (appliedFilter.startDate || appliedFilter.endDate);

  return (
    <div className="date-range-filter">
      <div
        className="date-filter-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h4 className="date-filter-title">{filter.label || fieldName}</h4>
        <div className="header-actions">
          {hasAppliedFilter && (
            <span className="applied-indicator" title="Currently applied">✓</span>
          )}
          {hasFilter && !hasAppliedFilter && isOnResultPage && (
            <span className="pending-indicator" title="Pending">●</span>
          )}
          <svg
            className={`expand-icon ${isExpanded ? 'expanded' : ''}`}
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="6,9 12,15 18,9" />
          </svg>
        </div>
      </div>

      {isExpanded && (
        <div className="date-filter-content">
          <div className="date-inputs">
            <div className="date-input-group">
              <CustomDatePicker
                value={startDate}
                onChange={handleStartDateChange}
                placeholder="Select start date"
                maxDate={maxDate}
                minDate={null}
                label="From Date"
                icon={true}
              />
            </div>

            <div className="date-input-group">
              <CustomDatePicker
                value={endDate}
                onChange={handleEndDateChange}
                placeholder="Select end date"
                maxDate={maxDate}
                minDate={startDate ? new Date(startDate) : null}
                label="To Date"
                icon={true}
              />
            </div>
          </div>

          {hasFilter && (
            <div className="date-actions">
              <button
                className="clear-date-button"
                onClick={handleClearFilter}
              >
                Clear Dates
              </button>
            </div>
          )}

          {filter.info && (
            <p className="date-filter-info">{filter.info}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default DateRangeFilter;